import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import AgoraRTC from "agora-rtc-sdk-ng";
import "./WebCall.css";

// ===== Configuration =====
const AGORA_APP_ID = "9178d4e4c4e54a16af4c3a0bfc05b637";
const WEB_USER_UID = 2;

// AppSync GraphQL endpoint — UPDATE to your actual AppSync endpoint
const APPSYNC_ENDPOINT =
  "https://ap4a42vmavdalagxpajfyfwpdq.appsync-api.ap-southeast-1.amazonaws.com/graphql";
const APPSYNC_API_KEY = "da2-gn2zifju5vfnvhqlwt6lg3454y";

// ===== Screens =====
const SCREEN = {
  LOADING: "loading",
  ERROR: "error",
  PRECALL: "precall",
  INCALL: "incall",
  ENDED: "ended",
};

const WebCall = () => {
  const { token } = useParams();

  // Screen state
  const [currentScreen, setCurrentScreen] = useState(SCREEN.LOADING);
  const [errorInfo, setErrorInfo] = useState({ title: "", message: "" });

  // Session state
  const [sessionData, setSessionData] = useState(null);
  const [userName, setUserName] = useState("");

  // Device state
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraPreviewOn, setCameraPreviewOn] = useState(false);

  // Call state
  const [callDuration, setCallDuration] = useState("00:00");
  const [finalDuration, setFinalDuration] = useState("--:--");
  const [remoteUserOnline, setRemoteUserOnline] = useState(false);
  const [remoteVideoOn, setRemoteVideoOn] = useState(false);
  const [remoteName, setRemoteName] = useState("Waiting for guru to join...");
  const [joinLoading, setJoinLoading] = useState(false);

  // Refs
  const clientRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const previewStreamRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const previewVideoRef = useRef(null);
  const localVideoContainerRef = useRef(null);
  const remoteVideoContainerRef = useRef(null);

  // ===== Validate meeting link on mount =====
  useEffect(() => {
    if (!token) {
      showError("Invalid Link", "No meeting token found in the URL.");
      return;
    }
    validateMeetingLink(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ===== Cleanup on unmount =====
  useEffect(() => {
    return () => {
      cleanupAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupAll = () => {
    stopCameraPreview();
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.stop();
      localAudioTrackRef.current.close();
    }
    if (localVideoTrackRef.current) {
      localVideoTrackRef.current.stop();
      localVideoTrackRef.current.close();
    }
    if (clientRef.current) {
      clientRef.current.leave().catch(() => {});
    }
  };

  // ===== Error handling =====
  const showError = (title, message) => {
    setErrorInfo({ title, message });
    setCurrentScreen(SCREEN.ERROR);
  };

  // ===== Validate Meeting Link via GraphQL =====
  const validateMeetingLink = async (meetingToken) => {
    try {
      const query = `
        mutation ValidateMeetingLink($token: String!) {
          validateMeetingLink(token: $token) {
            valid
            sessionId
            channelName
            agoraToken
            agoraAppId
            uid
            guruName
            sessionDuration
            error
          }
        }
      `;

      const response = await fetch(APPSYNC_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": APPSYNC_API_KEY,
        },
        body: JSON.stringify({
          query,
          variables: { token: meetingToken },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        console.error("GraphQL errors:", result.errors);
        showError(
          "Validation Failed",
          result.errors[0]?.message || "Unable to validate meeting link."
        );
        return;
      }

      const data = result.data?.validateMeetingLink;

      if (!data?.valid) {
        showError(
          "Cannot Join",
          data?.error || "The meeting link is invalid or has expired."
        );
        return;
      }

      const session = {
        sessionId: data.sessionId,
        channelName: data.channelName,
        agoraToken: data.agoraToken,
        agoraAppId: data.agoraAppId || AGORA_APP_ID,
        uid: data.uid || WEB_USER_UID,
        guruName: data.guruName || "Guru",
        sessionDuration: data.sessionDuration || 30,
      };

      setSessionData(session);
      setRemoteName(`Waiting for ${session.guruName} to join...`);

      // Start camera preview
      await startCameraPreview();

      setCurrentScreen(SCREEN.PRECALL);
    } catch (error) {
      console.error("Validation error:", error);
      showError(
        "Connection Error",
        "Unable to reach the server. Please check your internet connection."
      );
    }
  };

  // ===== Camera Preview =====
  const startCameraPreview = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      previewStreamRef.current = stream;
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
      }
      setCameraPreviewOn(true);
    } catch (err) {
      console.log("Camera preview not available:", err);
      setCameraEnabled(false);
      setCameraPreviewOn(false);
    }
  };

  const stopCameraPreview = () => {
    if (previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach((track) => track.stop());
      previewStreamRef.current = null;
    }
    setCameraPreviewOn(false);
  };

  // ===== Pre-Call Toggles =====
  const toggleCameraPreview = async () => {
    if (cameraEnabled) {
      stopCameraPreview();
      setCameraEnabled(false);
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = null;
      }
    } else {
      setCameraEnabled(true);
      await startCameraPreview();
    }
  };

  const toggleMicPreview = () => {
    setMicEnabled((prev) => !prev);
  };

  // ===== Join Call =====
  const joinCall = async () => {
    if (!sessionData) return;
    setJoinLoading(true);

    try {
      stopCameraPreview();

      // Create Agora client
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;

      // Setup event handlers
      setupAgoraEventHandlers(client);

      // Join channel
      await client.join(
        sessionData.agoraAppId,
        sessionData.channelName,
        sessionData.agoraToken,
        sessionData.uid
      );

      // Create and publish local tracks
      const tracks = [];

      if (micEnabled) {
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        localAudioTrackRef.current = audioTrack;
        tracks.push(audioTrack);
      }

      if (cameraEnabled) {
        const videoTrack = await AgoraRTC.createCameraVideoTrack();
        localVideoTrackRef.current = videoTrack;
        tracks.push(videoTrack);
        // Play in container after screen switch
        setTimeout(() => {
          if (localVideoContainerRef.current) {
            videoTrack.play(localVideoContainerRef.current);
          }
        }, 100);
      }

      if (tracks.length > 0) {
        await client.publish(tracks);
      }

      // Start timer
      callStartTimeRef.current = Date.now();
      timerIntervalRef.current = setInterval(() => {
        if (!callStartTimeRef.current) return;
        const elapsed = Date.now() - callStartTimeRef.current;
        const minutes = Math.floor(elapsed / 60000);
        const seconds = Math.floor((elapsed % 60000) / 1000);
        setCallDuration(
          `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
        );
      }, 1000);

      setCurrentScreen(SCREEN.INCALL);
      console.log("Successfully joined channel:", sessionData.channelName);
    } catch (error) {
      console.error("Join call error:", error);
      setJoinLoading(false);
      alert("Failed to join the call. Please try again. Error: " + error.message);
    }
  };

  // ===== Agora Event Handlers =====
  const setupAgoraEventHandlers = (client) => {
    client.on("user-published", async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      console.log("Subscribed to remote user:", user.uid, mediaType);

      if (mediaType === "video") {
        setRemoteVideoOn(true);
        setTimeout(() => {
          if (remoteVideoContainerRef.current) {
            user.videoTrack.play(remoteVideoContainerRef.current);
          }
        }, 100);
      }

      if (mediaType === "audio") {
        user.audioTrack.play();
      }
    });

    client.on("user-unpublished", (user, mediaType) => {
      console.log("Remote user unpublished:", user.uid, mediaType);
      if (mediaType === "video") {
        setRemoteVideoOn(false);
      }
    });

    client.on("user-left", (user) => {
      console.log("Remote user left:", user.uid);
      setRemoteUserOnline(false);
      setRemoteVideoOn(false);
      setRemoteName((prev) => {
        const guruName = sessionData?.guruName || "Guru";
        return `${guruName} has left the call`;
      });
    });

    client.on("user-joined", (user) => {
      console.log("Remote user joined:", user.uid);
      setRemoteUserOnline(true);
      setRemoteName(sessionData?.guruName || "Guru");
    });

    client.on("connection-state-change", (curState, prevState) => {
      console.log("Connection state:", prevState, "->", curState);
      if (curState === "DISCONNECTED") {
        handleEndCall();
      }
    });

    client.on("token-privilege-did-expire", () => {
      console.log("Token expired");
      alert("Your session has expired. Please rejoin using the meeting link.");
      handleEndCall();
    });
  };

  // ===== In-Call Controls =====
  const toggleMicInCall = async () => {
    const newState = !micEnabled;
    setMicEnabled(newState);

    if (localAudioTrackRef.current) {
      await localAudioTrackRef.current.setEnabled(newState);
    } else if (newState) {
      try {
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        localAudioTrackRef.current = audioTrack;
        await clientRef.current.publish(audioTrack);
      } catch (err) {
        console.error("Failed to create audio track:", err);
        setMicEnabled(false);
      }
    }
  };

  const toggleCameraInCall = async () => {
    const newState = !cameraEnabled;
    setCameraEnabled(newState);

    if (localVideoTrackRef.current) {
      await localVideoTrackRef.current.setEnabled(newState);
    } else if (newState) {
      try {
        const videoTrack = await AgoraRTC.createCameraVideoTrack();
        localVideoTrackRef.current = videoTrack;
        if (localVideoContainerRef.current) {
          videoTrack.play(localVideoContainerRef.current);
        }
        await clientRef.current.publish(videoTrack);
      } catch (err) {
        console.error("Failed to create video track:", err);
        setCameraEnabled(false);
      }
    }
  };

  // ===== End Call =====
  const handleEndCall = useCallback(async () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    let durationStr = "--:--";
    if (callStartTimeRef.current) {
      const elapsed = Date.now() - callStartTimeRef.current;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      durationStr = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    setFinalDuration(durationStr);

    try {
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }
      if (clientRef.current) {
        await clientRef.current.leave();
        clientRef.current = null;
      }
    } catch (err) {
      console.error("Cleanup error:", err);
    }

    setCurrentScreen(SCREEN.ENDED);
  }, []);

  // ===== SVG Icons =====
  const VideoIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
    </svg>
  );

  const VideoOffIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z" />
    </svg>
  );

  const MicIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
    </svg>
  );

  const MicOffIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
    </svg>
  );

  const EndCallIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
    </svg>
  );

  const GuruviceLogo = ({ size = 48 }) => (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      <circle cx={size / 2} cy={size / 2} r={size / 2} fill="#274546" />
      <text
        x={size / 2}
        y={size * 0.625}
        textAnchor="middle"
        fill="white"
        fontSize={size * 0.42}
        fontWeight="bold"
      >
        G
      </text>
    </svg>
  );

  // ===== Render Screens =====

  // Loading Screen
  if (currentScreen === SCREEN.LOADING) {
    return (
      <div className="wc-screen wc-loading-screen">
        <div className="wc-loading-container">
          <div className="wc-logo">
            <GuruviceLogo size={48} />
          </div>
          <h1>Guruvice</h1>
          <p className="wc-subtitle">Validating your meeting link...</p>
          <div className="wc-spinner" />
        </div>
      </div>
    );
  }

  // Error Screen
  if (currentScreen === SCREEN.ERROR) {
    return (
      <div className="wc-screen wc-error-screen">
        <div className="wc-error-container">
          <div className="wc-error-icon">⚠️</div>
          <h2>{errorInfo.title}</h2>
          <p>{errorInfo.message}</p>
          <button
            className="wc-btn wc-btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Pre-Call Screen
  if (currentScreen === SCREEN.PRECALL) {
    return (
      <div className="wc-screen wc-precall-screen">
        <div className="wc-precall-container">
          <div className="wc-logo-small">
            <GuruviceLogo size={32} />
            <span>Guruvice</span>
          </div>
          <h2>Ready to join?</h2>
          <p className="wc-session-info">
            Session with <strong>{sessionData?.guruName}</strong>
            <br />
            <span>Duration: {sessionData?.sessionDuration} minutes</span>
          </p>

          {/* Camera Preview */}
          <div className="wc-preview-container">
            <video
              ref={previewVideoRef}
              autoPlay
              muted
              playsInline
              style={{ display: cameraPreviewOn ? "block" : "none" }}
            />
            {!cameraPreviewOn && (
              <div className="wc-preview-placeholder">
                <div className="wc-avatar">👤</div>
                <p>Camera is off</p>
              </div>
            )}
          </div>

          {/* Device Controls */}
          <div className="wc-device-controls">
            <button
              className={`wc-device-btn ${cameraEnabled ? "active" : ""}`}
              onClick={toggleCameraPreview}
              title="Toggle Camera"
            >
              {cameraEnabled ? <VideoIcon /> : <VideoOffIcon />}
            </button>
            <button
              className={`wc-device-btn ${micEnabled ? "active" : ""}`}
              onClick={toggleMicPreview}
              title="Toggle Microphone"
            >
              {micEnabled ? <MicIcon /> : <MicOffIcon />}
            </button>
          </div>

          {/* Name Input */}
          <div className="wc-name-input-group">
            <label htmlFor="user-name">Your Name</label>
            <input
              type="text"
              id="user-name"
              placeholder="Enter your name"
              maxLength={50}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
          </div>

          <button
            className="wc-btn wc-btn-primary wc-btn-large"
            onClick={joinCall}
            disabled={joinLoading}
          >
            {joinLoading ? "Joining..." : "Join Call"}
          </button>
        </div>
      </div>
    );
  }

  // In-Call Screen
  if (currentScreen === SCREEN.INCALL) {
    return (
      <div className="wc-screen wc-call-screen">
        <div className="wc-call-container">
          {/* Video Grid */}
          <div className="wc-video-grid">
            {/* Remote video */}
            <div className="wc-video-container wc-remote">
              {!remoteVideoOn && (
                <div className="wc-video-placeholder">
                  <div className="wc-avatar-large">👤</div>
                  <p>{remoteName}</p>
                </div>
              )}
              <div
                ref={remoteVideoContainerRef}
                className="wc-video-player"
                style={{ display: remoteVideoOn ? "block" : "none" }}
              />
              <div className="wc-video-label">
                {sessionData?.guruName || "Guru"}
              </div>
            </div>
            {/* Local video */}
            <div className="wc-video-container wc-local">
              {cameraEnabled ? (
                <div ref={localVideoContainerRef} className="wc-video-player" />
              ) : (
                <div className="wc-video-placeholder">
                  <div className="wc-avatar-small">👤</div>
                </div>
              )}
              <div className="wc-video-label">You</div>
            </div>
          </div>

          {/* Call Timer */}
          <div className="wc-call-timer">
            <span>{callDuration}</span>
          </div>

          {/* Call Controls */}
          <div className="wc-call-controls">
            <button
              className={`wc-control-btn ${micEnabled ? "active" : ""}`}
              onClick={toggleMicInCall}
              title="Toggle Microphone"
            >
              {micEnabled ? <MicIcon /> : <MicOffIcon />}
            </button>
            <button
              className={`wc-control-btn ${cameraEnabled ? "active" : ""}`}
              onClick={toggleCameraInCall}
              title="Toggle Camera"
            >
              {cameraEnabled ? <VideoIcon /> : <VideoOffIcon />}
            </button>
            <button
              className="wc-control-btn wc-end-call"
              onClick={handleEndCall}
              title="Leave Call"
            >
              <EndCallIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Call Ended Screen
  if (currentScreen === SCREEN.ENDED) {
    return (
      <div className="wc-screen wc-ended-screen">
        <div className="wc-ended-container">
          <div className="wc-checkmark">✓</div>
          <h2>Call Ended</h2>
          <p>Duration: {finalDuration}</p>
          <p className="wc-ended-message">Thank you for using Guruvice!</p>
          <a href="https://guruvice.app" className="wc-btn wc-btn-primary">
            Go to Guruvice
          </a>
        </div>
      </div>
    );
  }

  return null;
};

export default WebCall;
